const normalizeStatus = (value) => String(value ?? '').trim().toLowerCase();
const normalizeText = (value) => String(value ?? '').trim();

/**
 * Lecture canonique, sans inférence, des traitements déclarés dans l'anamnèse.
 *
 * Le choix structuré prime toujours : « non » ferme explicitement la donnée,
 * même si un ancien champ contient encore du texte. Les anciens champs ne
 * servent que de reprise douce lorsqu'aucun choix structuré n'existe, ou si
 * « oui » a été sélectionné avant que le détail ne soit reporté.
 */
export function readCurrentTreatments(form) {
  const source = form && typeof form === 'object' ? form : {};
  const status = normalizeStatus(source.traitementsActuels);
  const detail = normalizeText(source.traitementsDetail);
  const moment = normalizeText(source.traitementsMomentPrise);
  const legacy = [...new Set([
    normalizeText(source.traitements),
    normalizeText(source.medicaments),
  ].filter(Boolean))].join('\n');

  if (status === 'non') {
    return Object.freeze({
      status: 'non',
      declared: false,
      text: '',
      moment: '',
      source: 'structured',
    });
  }

  if (status === 'oui') {
    return Object.freeze({
      status: 'oui',
      declared: true,
      text: detail || legacy,
      moment,
      source: detail ? 'structured' : (legacy ? 'legacy_fallback' : 'structured'),
    });
  }

  return Object.freeze({
    status: 'non_renseigne',
    declared: Boolean(legacy),
    text: legacy,
    moment: '',
    source: legacy ? 'legacy' : 'none',
  });
}

/**
 * Projection destinée aux consommateurs métier et aux prompts. Un ancien
 * champ texte ne devient jamais un traitement actif par sa seule présence :
 * seul le « oui » structuré posé par Anissa autorise le détail structuré.
 * Le legacy n'entre dans cette projection qu'après avoir été explicitement
 * recopié dans `traitementsDetail` par la relecture d'Anissa.
 */
export function projectActiveTreatments(form) {
  const treatments = readCurrentTreatments(form);
  const detail = normalizeText(form?.traitementsDetail);
  if (treatments.status !== 'oui' || !detail) {
    return Object.freeze({
      status: treatments.status,
      declared: false,
      text: '',
      moment: '',
      source: 'inactive',
    });
  }
  return Object.freeze({
    status: 'oui',
    declared: true,
    text: detail,
    moment: normalizeText(form?.traitementsMomentPrise),
    source: 'structured',
  });
}

export function formatCurrentTreatments(form, { includeMoment = false } = {}) {
  const treatments = readCurrentTreatments(form);
  if (!treatments.text) return '';
  if (!includeMoment || !treatments.moment) return treatments.text;
  return `${treatments.text}\nMoment habituel de prise : ${treatments.moment}`;
}

export function formatActiveTreatments(form, { includeMoment = false } = {}) {
  const treatments = projectActiveTreatments(form);
  if (!treatments.text) return '';
  if (!includeMoment || !treatments.moment) return treatments.text;
  return `${treatments.text}\nMoment habituel de prise : ${treatments.moment}`;
}
